import cloneDeep from 'lodash/cloneDeep';
import { ReactNode, RefObject, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { aiApi } from 'apis/ai';
import { SanitizeHTML } from 'app/utils/htmlSanitizer';
import { sanitizationProfiles } from 'app/utils/htmlSanitizer/profiles';
import { CopilotPinnedActions } from 'components/copilotPinnedActions';
import { ExpandText } from 'components/copilotPinnedActions/components/expandText';
import { ImproveReply } from 'components/copilotPinnedActions/components/improveReply';
import { MatchToStyleGuide } from 'components/copilotPinnedActions/components/matchToStyleGuide';
import { SpellingAndGrammar } from 'components/copilotPinnedActions/components/spellingAndGrammar';
import { EditorCopilotDropdown } from 'components/editorCopilotDropdown';
import { useGetComposeModalCopilotActions } from 'components/editorCopilotDropdown/utils/composeModalCopilotActions';
import { EditorMentions } from 'components/editorMentions';
import { EditorQuickRepliesDropdown } from 'components/editorQuickRepliesDropdown';
import type { ChangeSubjectFn } from 'components/editorQuickRepliesDropdown/editorQuickRepliesDropdown.types';
import { HookFormRichTextField, RichTextFieldRef } from 'components/hookForm';
import { copilotUtils } from 'utils/copilot';

import type { FormValues } from '../composeEmailModal.types';
import type { AttachmentUpload } from '../hooks/useAttachments';

import styles from './composeEmailFieldRichText.module.scss';

type ComposeEmailFieldRichTextProps = {
  name: 'body' | 'internalNote';
  label: string;
  placeholder: string;
  required?: boolean;
  editorRef?: RefObject<RichTextFieldRef>;
  onChangeSubject?: ChangeSubjectFn;
  attachmentsFields?: ReactNode;
  onAttachmentAdded?: (attachment: AttachmentUpload) => void;
  customWindow?: Window;
  enduserId?: string | null;
  csid?: number | null;
};

export const ComposeEmailFieldRichText = ({
  name,
  label,
  placeholder,
  required = false,
  editorRef,
  onChangeSubject,
  attachmentsFields,
  onAttachmentAdded,
  customWindow,
  enduserId = null,
  csid = null,
}: ComposeEmailFieldRichTextProps): JSX.Element => {
  const { control } = useFormContext<FormValues>();
  const text = useWatch({ name, control }) || '';
  const { data: styleGuide } = aiApi.useReadAIWritingStyleGuideMatchQuery(
    {
      features: 'impr_reply',
      channels: 'email',
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const { editorCopilotActions, internalNoteEditorCopilotActions } =
    useGetComposeModalCopilotActions('email', () => {
      const hasStyleGuide = !!styleGuide?.data?.match;
      const { isDisabled, tooltipContent } =
        copilotUtils.matchToStyleGuide.getStatus({
          text,
          channel: 'email',
          hasStyleGuideForChannel: hasStyleGuide,
        });

      if (isDisabled) {
        return tooltipContent;
      }

      return false;
    });

  const isNote = name === 'internalNote';
  const copilotActions = isNote
    ? internalNoteEditorCopilotActions
    : editorCopilotActions;
  const editorClassName = isNote
    ? styles.internalNoteEditor
    : styles.messageEditor;

  const sanitize = useCallback((html: string) => {
    const profile = cloneDeep(sanitizationProfiles['email']);
    profile.allowedAttributes = {
      ...profile.allowedAttributes,
      span: ['contenteditable', 'data-variable-name', 'data-variable-label'],
    };

    return SanitizeHTML.customSanitize(html, profile, true);
  }, []);

  return (
    <HookFormRichTextField
      name={name}
      label={label}
      control={control}
      contentType="html"
      editorRef={editorRef}
      rules={
        required
          ? {
              required: 'Input is required',
            }
          : undefined
      }
      componentProps={{
        className: editorClassName,
        placeholder,
        showCharacterLimit: false,
        channel: 'email',
        sanitize,
        hasBorder: true,
        editorWindow: customWindow,
        hideSendOnEnterToggle: true,
        disableSubmitMessageWithEnter: true,
        attachmentsFields,
        onAttachmentAdded,
        toolbarAddons: (
          <>
            {isNote && <EditorMentions />}
            <EditorQuickRepliesDropdown
              enduserId={enduserId}
              csid={csid}
              changeSubject={onChangeSubject}
              customWindow={customWindow}
            />
            <EditorCopilotDropdown
              editorCopilotActions={copilotActions}
              channel="email"
            />
            <CopilotPinnedActions
              channel="email"
              text={text}
              pinnableActions={[
                {
                  id: 'improveReply',
                  component: ImproveReply,
                  condition: (args) =>
                    args.flags.agentCopilotPinActions &&
                    args.pinState['improveReply'],
                  label: 'Improve message',
                  disabledImprovements: ['match_tone'],
                },
                {
                  id: 'improveReplyMatchStyleGuide',
                  component: MatchToStyleGuide,
                  condition: (args) =>
                    args.flags.agentCopilotPinActions &&
                    args.pinState['improveReplyMatchStyleGuide'],
                },
                {
                  id: 'checkSpellingAndGrammar',
                  component: SpellingAndGrammar,
                  condition: (args) =>
                    args.flags.agentCopilotPinActions &&
                    args.pinState['checkSpellingAndGrammar'],
                },
                {
                  id: 'expandText',
                  component: ExpandText,
                  condition: (args) =>
                    args.flags.agentCopilotPinActions &&
                    args.pinState['expandText'],
                  label: 'Expand message',
                },
              ]}
            />
          </>
        ),
      }}
    />
  );
};
